/* eslint-disable no-console */
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, Skeleton, useDisclosure } from '@chakra-ui/react'

import { IAdditionalServices, IFetchedAdditionalServices, IFetchedOfferingsSelected, IOfferingsSelected, IResponseGeneric, ISaveOffering } from 'services/IApi.d'
import { getAdditionalServicesPlan, getOffering, saveOfferings, updateOfferings } from 'services/api'
import FooterButtons from 'components/common/FooterButtons/FooterButtons'
import { useOfferingProvider } from 'providers/offeringProvider/OfferingProvider'
import AdditionalService from 'components/offering/AdditionalService/AdditionalService'
import SummaryPlan from 'components/offering/Summary/SummaryPlan'
import { useGlobalContext } from 'providers/GlobalProvider'
import IBalert from 'components/common/IBalert/IBalert'
import { isSuccessStatus } from 'utils/functions/validations'
import IBModalAction from 'components/common/IBModalAction/IBModalAction'
import useGa from 'hooks/ga/useGa'

const AdditionalServicesPage = () => {
  const navigate = useNavigate()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenInfo, onOpen: onOpenInfo, onClose: onCloseInfo } = useDisclosure()
  const { selectedOfferings, setSelectedOfferings, changeLoadingAdditionalServices, loading, changePlan } = useOfferingProvider()
  const { idEmpresa, idSuscriptor } = useGlobalContext()
  const { sendDataLayer } = useGa()
  const [additionalServices, setAdditionalServices] = useState<IAdditionalServices[]>([])
  const [loadingNext, setLoadingNext] = useState<boolean>(false)
  const [savedServices, setSavedServices] = useState<IOfferingsSelected[]>([])
  const [isChangePlan, setIschangePlan] = useState<boolean>(false)

  const title = '¡Potenciá tu plan con servicios adicionales!'
  const titleSub = 'Vas a poder usar estos servicios en todas las cuentas elegidas para tu plan.'
  const primaryText = 'Continuar'
  const secondaryText = 'Volver'
  const tertiaryText = 'Omitir'
  const textModalTitleInfo = 'Seleccioná servicios adicionales'
  const textModalBodyInfo = 'Cada plan tiene sus propios servicios adicionales. Por eso, incluimos una lista actualizada para el plan que elegiste.'

  const resetValueServicesAditional = () => {
    const savedServicesCodes = savedServices.map(service => service.codeDn)
    const savedAdditionalServices = additionalServices.filter(service =>
      savedServicesCodes.includes(service.codeDn)
    )
    setSelectedOfferings(prevState => ({ ...prevState, additionalServices: savedAdditionalServices }))
  }

  const handleBack = () => {
    resetValueServicesAditional()
    navigate('/cuentas')
  }
  const handleSkip = () => {
    resetValueServicesAditional()
    navigate('/resumen', { state: { pageBefore: 'additionalServices', edit: true } })

  }

  const getData = async () => {
    changeLoadingAdditionalServices(true)
    try {
      const [getServices, getSavedServices]: [IFetchedAdditionalServices, IFetchedOfferingsSelected] = await Promise.all([
        getAdditionalServicesPlan(selectedOfferings.plan.codeDn),
        getOffering(idSuscriptor, idEmpresa)
      ])
      if (!isSuccessStatus(getServices.status, getSavedServices.status)) {
        navigate('/error')
        throw new Error('Error al obtener la lista de servicios adicionales seleccionados')
      }

      return { getServices: getServices?.data, getSavedServices: getSavedServices?.data }
    } catch (error) {
      navigate('/error')
      return error
    }
  }

  const removeAllAdditionalServices = async () => {
    const currentOffering: ISaveOffering = {
      codeDn: selectedOfferings.plan.codeDn,
      subscriberId: idSuscriptor,
      companyId: idEmpresa,
      additionalService: false,
    }

    const response = await updateOfferings([currentOffering])
    return response
  }

  // saved additional services saved by the user 
  const savedServicesApi = async () => {
    const servicesToAdd = selectedOfferings.additionalServices.map(service => ({
      codeDn: service.codeDn,
      additionalService: true,
      subscriberId: idSuscriptor,
      companyId: idEmpresa,
      type: service.type,
    }))
    const nameServiceToAdd = selectedOfferings.additionalServices.map(service => service.denomination).join(' - ')
    const isUpdate = savedServices.length > 0

    if (!isUpdate && servicesToAdd.length === 0) {
      navigate('/resumen', { state: { pageBefore: 'additionalServices' } })
      return
    }

    try {
      let response: IResponseGeneric
      if (isUpdate && servicesToAdd.length === 0) {
        response = await removeAllAdditionalServices()
      } else {
        response = isUpdate ? await updateOfferings(servicesToAdd) : await saveOfferings(servicesToAdd)
      }

      if (isSuccessStatus(response?.status)) {
        if (servicesToAdd.length > 0) {
          sendDataLayer({
            event: 'completed_onboarding_step',
            onboarding_step: 'Selección de adicionales',
            products: nameServiceToAdd,
          })
        }
        setLoadingNext(false)
        navigate('/resumen', { state: { pageBefore: 'additionalServices', edit: true } })
      } else {
        onOpen()
        setLoadingNext(false)
      }
    } catch (error) {
      console.error('Error al guardar servicios adicionales:', error)
      onOpen()
      setLoadingNext(false)
    }
  }

  const isSelected = serviceCode => savedServices.some(service => service.codeDn === serviceCode)

  const handleContinue = async () => {
    setLoadingNext(true)
    savedServicesApi()
  }

  useEffect(() => {
    getData()
      .then(data => {
        setAdditionalServices(data.getServices)
        const filteredServices: IOfferingsSelected[] = data.getSavedServices.filter(service => service.additionalService)
        setSavedServices(filteredServices)
        changeLoadingAdditionalServices(false)
        if (changePlan) {
          setIschangePlan(true)
          setSelectedOfferings(prevState => ({ ...prevState, additionalServices: [] }))
        }
      })
      .catch(() => {
        navigate('/error')
      })
  }, [])

  useEffect(() => {
    if (additionalServices?.length > 0 && savedServices?.length > 0) {
      const savedServicesCodes = savedServices.map(service => service.codeDn)
      const savedAdditionalServices = additionalServices.filter(service =>
        savedServicesCodes.includes(service.codeDn)
      )
      setSelectedOfferings(prevState => ({ ...prevState, additionalServices: savedAdditionalServices }))
    }
  }, [additionalServices, savedServices, setSelectedOfferings])

  useEffect(() => {
    if (isChangePlan) {
      onOpenInfo()
    }
  }, [isChangePlan])

  return (
    <div>
      <Box>
        <Box textStyle='h1' color='grey.120' mb={2} data-testid='title'>{title}</Box>
        <Box textStyle='bodyLg' mb={7} data-testid='subtitle'>{titleSub}</Box>
        <Flex>
          <Box flex={0.66} height='45vh' overflow='auto' pr={4} pb={4}>
            {!loading.current.additionalServices ?
              additionalServices?.map((service: IAdditionalServices) => (
                <AdditionalService
                  idKey={service.codeDn}
                  service={service}
                  selected={isSelected(service.codeDn)}
                />
              ))
              :
              <>
                <Skeleton width='100%' height={120} marginBottom='12px' />
                <Skeleton width='100%' height={120} marginBottom='12px' />
                <Skeleton width='100%' height={120} />
              </>
            }
          </Box>
          <Box flex={0.34}>
            <SummaryPlan
              resetValueServicesAditional={resetValueServicesAditional}
              step='additionalServices'
            />
          </Box>
        </Flex>
        <FooterButtons
          onBack={handleBack}
          onNext={handleContinue}
          onSkip={handleSkip}
          // nextDisabled={isButtonDisabled}
          loading={loadingNext}
          backText={secondaryText}
          nextText={primaryText}
          skipText={tertiaryText}
        />
      </Box>
      <IBalert
        isOpen={isOpen}
        onClose={onClose}
        title='No podemos guardar tu selección'
        text='Estamos trabajando para solucionarlo, volvé a intentar más tarde.'
        buttonClose
        type='error'
        variant='sm'
      />
      <IBModalAction
        isOpen={isOpenInfo}
        onClose={onCloseInfo}
        buttonActionText='Entendido'
        buttonAction={onCloseInfo}
        title={textModalTitleInfo}
        textBody={textModalBodyInfo}
      />
    </div>
  )
}

export default AdditionalServicesPage